import React, { useState, useEffect, useContext, FC } from 'react';
import BadgeCarousel from './BadgeCarousel';
import Link from 'next/link';
import { useRouter, NextRouter } from 'next/router';
import { theme, websiteColors } from '../../themes/Theme';
import { quizhubUrl } from '../../config/config.js';
import { certhubUrl } from '../../config/config.js';
import AuthContext from '../../contexts/AuthContext';
import { errorToaster, successToaster } from '../../utils/utils';
import { subscribeUser, getUserById } from '../../crud/user.crud.js';
import { useFormik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import AnimatedLogo from '../../common/AnimatedLogo';
import revampData from '../../data/revampData.json';

interface FormValues {
  email_id: string;
}

const HomeFooter: FC = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [isSubscribed, setIsSubscribed] = useState<boolean>(true);
  const [userEmail, setUserEmail] = useState<string>('');
  // Since revampData is imported from JSON, cast to any to access properties
  const locationAndCategoriesData = (revampData as any).data
    .listLocationCategoryConfigs.data[0];

  const router: NextRouter = useRouter();
  const { signedIn } = useContext(AuthContext) as { signedIn: boolean };

  const clientType = 1;
  const subscriptionSource: number =
    router.route === '/'
      ? 1
      : router.route === '/resources'
        ? 5
        : router.route === '/communities'
          ? 4
          : 1;

  useEffect(() => {
    if (signedIn === true) {
      getProfile();
    } else if (
      signedIn === false &&
      (router.route === '/' ||
        router.route === '/resources' ||
        router.route === '/communities')
    ) {
      const timer = setTimeout(() => {
        setIsSubscribed(false);
      }, 60000);
      return () => clearTimeout(timer);
    }
  }, [signedIn, router.route]);

  const getProfile = async (): Promise<void> => {
    try {
      const res: any = await getUserById();
      if (res.data.email_subscription) {
        setIsSubscribed(true);
      }
      if (
        res.data.email_subscription === false &&
        (router.route === '/' ||
          router.route === '/resources' ||
          router.route === '/communities')
      ) {
        const timer = setTimeout(() => {
          setIsSubscribed(false);
        }, 60000);
        setUserEmail(res.data.email_id);
        clearTimeout(timer);
      }
    } catch (err) {
      console.error(err);
      setIsSubscribed(false);
    }
  };

  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: { email_id: userEmail },
    validationSchema: Yup.object({
      email_id: Yup.string()
        .required('Email is required')
        .email('Please enter a valid email'),
    }),
    onSubmit: async (
      values: FormValues,
      { resetForm }: FormikHelpers<FormValues>
    ) => {
      setLoading(true);
      try {
        const res: any = await subscribeUser(
          values.email_id,
          clientType,
          subscriptionSource
        );
        if (res.status === 200) {
          successToaster('Subscribed Successfully');
        }
        if (signedIn === true) {
          await getProfile();
        }
        resetForm();
        setLoading(false);
        setIsSubscribed(true);
      } catch (err) {
        console.error(err);
        errorToaster('Something went wrong, could not subscribe user');
        setLoading(false);
        setIsSubscribed(false);
      }
    },
  });

  // Show error toaster when email validation fails
  useEffect(() => {
    if (formik.touched.email_id && formik.errors.email_id) {
      errorToaster(formik.errors.email_id);
      setLoading(false);
    }
  }, [formik.touched.email_id, formik.errors.email_id]);

  const style: React.CSSProperties = {
    textDecoration: 'none',
    color: 'white',
  };

  return (
    <footer className="footer-container font-figtree text-white py-4">
      <div className="px-lg-5 px-md-5  px-4   py-3">
        <div className="row  pe-0 ps-0  mb-5 ">
          <div className="col-xl-4 col-lg-12 col-12 mb-4 mr-1 ps-0 brand-leftcontainer">
            <div className="d-flex flex-column gap-1">
              <Link href="/" style={{ width: '120px' }}>
                <AnimatedLogo color="white" customClass="mb-2" />
              </Link>
              <p>AI-Powered Event Platform</p>
            </div>
            <p className="my-4 brand-descr">
              KonfHub is an AI-powered, GDPR-compliant platform for seamless
              ticketing, secure attendee management, and smooth event
              operations. Say goodbye to complexity and hello to seamless,
              powerful event management!
            </p>
            <img
              src="/img/gdrp-logo.svg"
              alt="GDPR Certified"
              className="footer-gdpr mt-3"
            />
          </div>

          <div className="col-xl-8 col-lg-12 col-12">
            <div className="row footer-links">
              {/* KONFHUB Column */}
              <div
                className="footer-column col-6 col-sm-2 px-0 col-xl-2 mb-4"
                style={{ minWidth: '156px' }}
              >
                <h4 className="footer-typo-heading">KONFHUB</h4>
                <ul className="list-unstyled d-flex flex-column gap-2">
                  <li>
                    <Link style={style} href="https://konfhub.com/pricing">
                      Pricing
                    </Link>
                  </li>
                  <li>
                    <Link style={style} href="https://konfhub.com/features">
                      Features
                    </Link>
                  </li>
                  <li className="comprehensive-txt">
                    <Link
                      style={style}
                      href="https://konfhub.com/payments-settlements"
                    >
                      Payments & Settlements
                    </Link>
                  </li>
                  <li className="comprehensive-txt">
                    <Link style={style} href="https://konfhub.com/ticketing">
                      Comprehensive Ticketing
                    </Link>
                  </li>
                  <li>
                    <Link
                      style={style}
                      href="https://konfhub.com/whitelabeling"
                    >
                      White Labeling
                    </Link>
                  </li>
                  <li>
                    <Link style={style} href="https://konfhub.com/usecases">
                      Use Cases
                    </Link>
                  </li>
                  <li>
                    <Link style={style} href="https://konfhub.com/developers">
                      For Developers
                    </Link>
                  </li>
                  <li>
                    <Link style={style} href="https://konfhub.com/stripe">
                      Stripe
                    </Link>
                  </li>
                  <li>
                    <Link style={style} href="https://konfhub.com/tap-payments">
                      Tap Payments
                    </Link>
                  </li>
                  <li>
                    <Link style={style} href="https://konfhub.com/logos">
                      Brand Assets
                    </Link>
                  </li>
                </ul>
              </div>

              {/* LEGAL & POLICIES Column */}
              <div
                className="footer-column lp-column col-6 col-sm-2 px-0 col-lg-2 col-xl-2 mb-4"
                style={{ minWidth: '135px', maxWidth: '130px' }}
              >
                <h4 className="footer-typo-heading text-nowrap">
                  LEGAL & POLICIES
                </h4>
                <ul className="list-unstyled d-flex flex-column gap-2">
                  <li>
                    <Link
                      style={style}
                      href="https://konfhub.com/code-of-conduct"
                    >
                      Code of Conduct
                    </Link>
                  </li>
                  <li>
                    <Link
                      style={style}
                      href="https://konfhub.com/privacy-policy"
                    >
                      Privacy Policy
                    </Link>
                  </li>
                  <li className="join-txt">
                    <Link
                      style={style}
                      href="https://konfhub.com/terms-and-conditions"
                    >
                      Terms & Conditions
                    </Link>
                  </li>
                  <li>
                    <Link
                      style={style}
                      href="https://konfhub.com/responsible-disclosure"
                    >
                      Responsible Disclosure
                    </Link>
                  </li>
                  <li>
                    <Link
                      style={style}
                      href="https://konfhub.com/cancellation-refund-policy"
                    >
                      Cancellation & Refund Policy
                    </Link>
                  </li>
                </ul>
              </div>

              {/* SOLUTIONS Column */}
              <div
                className="footer-column col-6 col-sm-2 mx-lg-2 ml-2 px-0 col-xl-2 mb-4"
                style={{ minWidth: '101px' }}
              >
                <div className="d-flex flex-column justify-content-between custom-gap">
                  <div>
                    <h4 className="footer-typo-heading">SOLUTIONS</h4>
                    <ul className="list-unstyled d-flex flex-column gap-2">
                      <li>
                        <Link style={style} href="https://konfhub.com/aibooth">
                          AI Photo Booth
                        </Link>
                      </li>
                      <li>
                        <Link
                          style={style}
                          href="https://konfhub.com/aigallery"
                        >
                          AI Photo Gallery
                        </Link>
                      </li>
                      <li>
                        <Link
                          style={style}
                          href="https://konfhub.com/attendeeapp"
                        >
                          Attendee App
                        </Link>
                      </li>
                      <li>
                        <Link
                          target="_blank"
                          style={style}
                          href={`${certhubUrl}`}
                        >
                          CertHub
                        </Link>
                      </li>
                      <li>
                        <Link
                          style={style}
                          href="https://konfhub.com/checkinapp"
                        >
                          Check-In App
                        </Link>
                      </li>
                      <li>
                        <Link
                          style={style}
                          href="https://konfhub.com/exhibitor-portal"
                        >
                          Exhibitor Portal
                        </Link>
                      </li>
                      <li>
                        <Link
                          style={style}
                          href="https://konfhub.com/sponsorapp"
                        >
                          Sponsor App
                        </Link>
                      </li>
                      <li>
                        <Link
                          target="_blank"
                          style={style}
                          href={`${quizhubUrl}`}
                        >
                          QuizHub
                        </Link>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* EXPLORE Column */}
              <div
                className="footer-column col-6 col-sm-2 mx-lg-2 ml-2 px-0 col-xl-2 mb-4"
                style={{ minWidth: '95px' }}
              >
                <div className="d-flex flex-column justify-content-between custom-gap">
                  <div>
                    <h4 className="footer-typo-heading">EXPLORE</h4>
                    <ul className="list-unstyled d-flex flex-column gap-2 margin-bottom-explore">
                      <li>
                        <Link style={style} href="https://konfhub.com/events?category=Marathon">
                          Events
                        </Link>
                      </li>
                      <li>
                        <Link
                          style={style}
                          href="https://konfhub.com/partner-with-us"
                        >
                          Partner With Us
                        </Link>
                      </li>
                      <li>
                        <Link
                          style={style}
                          href="https://konfhub.com/case-studies"
                        >
                          Case Studies
                        </Link>
                      </li>
                      <li>
                        <a
                          style={style}
                          target="_blank"
                          href="https://blog.konfhub.com/"
                        >
                          Blogs
                        </a>
                      </li>
                      <li>
                        <a
                          style={style}
                          target="_blank"
                          href="https://www.youtube.com/@KonfHubTech"
                        >
                          Videos
                        </a>
                      </li>
                      <li>
                        <Link
                          style={style}
                          href="https://konfhub.com/resources"
                        >
                          Resources
                        </Link>
                      </li>
                    </ul>
                  </div>
                  <div className="d-lg-block d-md-none d-none">
                    <h4 className="footer-typo-heading">COMPANY</h4>
                    <ul className="list-unstyled d-flex flex-column gap-2">
                      <li>
                        <Link style={style} href="https://konfhub.com/about-us">
                          About Us
                        </Link>
                      </li>
                      <li>
                        <Link
                          style={style}
                          href="https://konfhub.com/community"
                        >
                          Join Our Community
                        </Link>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* COMPANY Column */}
              <div className="footer-column col-6 col-sm-2 mx-2 px-0 col-xl-2 mb-4 d-lg-none d-md-block">
                <h4 className="footer-typo-heading">COMPANY</h4>
                <ul className="list-unstyled d-flex flex-column gap-2">
                  <li>
                    <Link style={style} href="#">
                      About Us
                    </Link>
                  </li>
                  <li className="join-txt">
                    <Link style={style} href="https://konfhub.com/events">
                      Join Our Community
                    </Link>
                  </li>
                </ul>
              </div>

              {/* COMPARE US WITH Column */}
              <div
                className="footer-column col-6 col-sm-2 px-0 col-lg-2 col-xl-2 mb-4"
                style={{ minWidth: '175px' }}
              >
                <div>
                  <h4 className="footer-typo-heading text-nowrap">
                    COMPARE WITH
                  </h4>
                  <ul className="list-unstyled d-flex flex-column gap-2">
                    <li>
                      <a
                        target="_blank"
                        style={style}
                        href="https://blog.konfhub.com/konfhub-vs-townscript"
                      >
                        Townscript
                      </a>
                    </li>
                    <li>
                      <a
                        target="_blank"
                        style={style}
                        href="https://blog.konfhub.com/konfhub-vs-eventbrite"
                      >
                        Eventbrite
                      </a>
                    </li>
                    <li>
                      <a
                        target="_blank"
                        style={style}
                        href="https://blog.konfhub.com/platinumlistnet-vs-konfhub-which-event-ticketing-platform-is-right-for-you?showSharer=trueunder"
                      >
                        Platinumlist
                      </a>
                    </li>
                  </ul>
                </div>
                <div className="d-lg-block d-md-none d-none contact-us-column">
                  <h4 className="footer-typo-heading">CONTACT US</h4>
                  <ul className="list-unstyled d-flex flex-column gap-2">
                    <li>
                      <a style={style} href="mailto:support@konfhub.com">
                        Support
                      </a>{' '}
                    </li>
                    <li>
                      <a
                        style={style}
                        target="_blank"
                        href="https://calendly.com/konfhub/konfhub?month=2024-10"
                      >
                        Schedule a Demo
                      </a>
                    </li>
                  </ul>
                </div>
              </div>

              {/* CONTACT US Column */}
              <div className="footer-column m-autto mr-md-auto col-6 col-sm-2 mx-2 px-0 col-xl-2 mb-4 mr-md-auto d-lg-none d-md-block">
                <h4 className="footer-typo-heading">CONTACT US</h4>
                <ul className="list-unstyled d-flex flex-column gap-2">
                  <li>
                    <a style={style} href="mailto:support@konfhub.com">
                      Support
                    </a>{' '}
                  </li>
                  <li>
                    <a
                      style={style}
                      target="_blank"
                      href="https://calendly.com/konfhub/konfhub?month=2024-10"
                    >
                      Schedule a Demo
                    </a>
                  </li>
                </ul>
              </div>
            </div>
            {/* Uncomment and implement if needed:
            <div className='locationCategory-container'>
              <LocationCountryConfig
                heading={locationAndCategoriesData.locationSection.sectionHeading}
                items={locationAndCategoriesData.locationSection.locationDetails}
                itemKeyName="locationName"
                itemUrlKeyName="locationUrl"
              />
              <LocationCountryConfig
                heading={locationAndCategoriesData.categoriesSection.sectionHeading}
                items={locationAndCategoriesData.categoriesSection.categoryDetail}
                itemKeyName="categoryName"
                itemUrlKeyName="categoryUrl"
              />
            </div>
            */}
          </div>
        </div>
        {/* Second row: Newsletter and recognitions */}
        <div className="row align-items-start my-4">
          <div className="col-lg-8 col-md-6 text-center p-0 text-md-start mb-4 mb-md-0">
            <div className="d-flex flex-column gap-2 align-items-start">
              <h4 className="footer-typo-heading mb-2">RECOGNITIONS</h4>
              <div
                style={{ width: '100%' }}
                className="d-flex badge-scrll-container flex-wrap justify-content-between justify-content-md-start gap-3 "
              >
                <BadgeCarousel />
              </div>
            </div>
          </div>

          <div className="col-lg-4 col-md-6 p-0">
            <div className="d-flex flex-column gap-2">
              <h4 className="footer-typo-heading">JOIN OUR NEWSLETTER</h4>
              <div>
                <form className="d-flex input-btn-container">
                  <input
                    type="email"
                    placeholder="Enter your email"
                    className="form-control me-2 pl-1"
                    aria-label="email@website.com"
                    aria-describedby="button-addon2"
                    onChange={(e) =>
                      formik.setFieldValue('email_id', e.target.value)
                    }
                    value={formik.values.email_id}
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setLoading(true);
                      formik.handleSubmit();
                    }}
                    disabled={loading}
                    className="btn btn-light"
                  >
                    Subscribe
                  </button>
                </form>
                <p className="mt-2 newsletter-nospam-txt">
                  * No spam &quot;we promise&quot;!✌️{' '}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="row border-top d-flex flex-row justify-content-between align-items-center pt-4">
          <div className="col-md-6 ps-0 pe-0 col-6">
            <p className="brand-descr-b text-nowrap">
              © {new Date().getFullYear()} KonfHub. All rights reserved.
            </p>
          </div>

          <div className="col-md-6 col-6 ps-0 pe-0 text-center text-md-end social-container">
            <Link
              href="https://whatsapp.com/channel/0029VbBQhG7Au3aPV4H6PC0p"
              target="_blank"
              className="me-sm-3 me-2"
            >
              <img
                src="/img/whatsapp.svg"
                alt="whatsapp"
                className="social-icons"
              />
            </Link>
            <Link
              href="https://twitter.com/konfhub"
              target="_blank"
              className="me-sm-3 me-2"
            >
              <img src="/img/x-twitter.svg" alt="X" className="social-icons" />
            </Link>
            <Link
              href="https://www.linkedin.com/company/konfhub/"
              target="_blank"
              className="me-sm-3 me-2"
            >
              <img
                src="/img/linkedIn-logo.svg"
                alt="LinkedIn"
                className="social-icons"
              />
            </Link>
            <Link
              href="https://www.facebook.com/konfhub/"
              target="_blank"
              className="me-sm-3 me-2"
            >
              <img
                src="/img/fb-logo.svg"
                alt="Facebook"
                className="social-icons"
              />
            </Link>
            <Link
              href="https://www.instagram.com/konfhubevents/"
              target="_blank"
              className="me-sm-3 me-2"
            >
              <img
                src="https://d2nnrpx7gk2my2.cloudfront.net/files/672c650e97b04e000924da1b/Socialicon.svg"
                alt="Instagram"
                className="social-icons"
              />
            </Link>
            <Link
              href="https://www.youtube.com/@KonfHubTech"
              target="_blank"
              className="me-sm-3 me-2"
            >
              <img
                src="/img/youtube2.svg"
                alt="youtube"
                className="social-icons"
              />
            </Link>
          </div>
        </div>
      </div>

      <style jsx>{`
        .container {
          max-width: 1200px;
          width: 100%;
        }
        .margin-bottom-explore {
          margin-bottom: 18px;
        }
        .brand-descr {
          font-size: 16px;
          font-weight: 400;
          line-height: 27.2px;
          color: #d6d6d6;
          max-width: 262px;
        }
        .footer-typo-heading {
          font-size: 14px;
          font-weight: 700;
          line-height: 20px;
          letter-spacing: 2px;
        }
        .locationCategory-container {
          margin: -12px;
        }
        .list-unstyled li {
          font-size: 16px;
          font-weight: 400;
          line-height: 24px;
        }
        .brand-leftcontainer {
          max-width: 350px;
          width: 100%;
        }
        .badge-img {
          height: 67.2px;
          width: 60px;
        }
        .footer-container {
          background-color: ${websiteColors[theme].primaryWebsiteTheme};
          background-image: url('/img/footer-bg.svg');
          background-position: center;
          background-size: cover;
          background-repeat: no-repeat;
        }
        .input-btn-container {
          border: 1px solid #d0d5dd;
          box-shadow: 0px 1px 2px rgba(16, 24, 40, 0.05);
          border-radius: 5px;
          padding: 4px 4px;
        }
        .brand-leftcontainer {
          max-width: 350px;
          width: 100%;
        }
        input.form-control button.btn {
          padding-top: 5px !important;
          padding-bottom: 5px !important;
        }
        .form-control {
          border: 0 !important;
          background-color: transparent !important;
          color: #fff;
        }
        .newsletter-nospam-txt {
          font-size: 15px;
          font-weight: 400;
          line-height: 24px;
          letter-spacing: 1px;
        }
        .link {
          text-decoration: none !important;
          color: #fff !important;
        }
        .form-control:focus {
          box-shadow: 0 0 0 !important;
        }

        @media (max-width: 1200px) {
          .brand-leftcontainer {
            max-width: 100% !important;
            width: 100%;
          }
        }

        .footer-container {
          /* background-image:none; */
        }

        @media screen and (max-width: 1201px) {
          .brand-descr {
            max-width: 100%;
          }
        }
        .badge-scrll-container {
          max-width: 650px;
        }

        @media screen and ((min-width: 992px) and (max-width: 1225px)) {
          .badge-scrll-container {
            max-width: 550px;
          }
        }
        @media screen and (min-width: 1024px) {
          .contact-us-column {
            margin-top: 136px;
          }
        }
        @media (min-width: 768px) and (max-width: 992px) {
          .footer-links {
            display: flex;
            flex-wrap: wrap;
            justify-content: space-between;
          }
          .m-autto {
            margin-right: auto !important;
          }
          .footer-container {
            background-image: none;
          }
          .footer-column:nth-child(1) {
            order: 1;
            min-width: 150px !important;
          }
          .footer-column:nth-child(2) {
            order: 2;
            min-width: 160px !important;
          }
          .footer-column:nth-child(3) {
            order: 3;
          }
          .footer-column:nth-child(4) {
            order: 4;
          }
          .footer-column:nth-child(5) {
            order: 5;
          }
          .footer-column:nth-child(6) {
            order: 6;
          }
          .footer-column:nth-child(7) {
            order: 7;
          }
          .join-txt {
            max-width: 77px;
          }
          .badge-scrll-container {
            max-width: 300px;
          }
        }
        @media (min-width: 1440px) {
          .lp-column {
            min-width: 226px !important;
          }
        }

        @media (max-width: 576px) {
          .footer-column:nth-child(1) {
            min-width: 146px !important;
          }
          .footer-links {
            display: flex;
            flex-wrap: wrap;
          }
          .footer-column:nth-child(1),
          .footer-column:nth-child(2) {
            order: 1;
            width: 50%;
            min-width: 147px;
          }
          .footer-column:nth-child(3),
          .footer-column:nth-child(4) {
            order: 2;
            width: 50%;
            margin: 5px 0px !important;
          }
          .footer-column:nth-child(5),
          .footer-column:nth-child(6) {
            order: 3;
            width: 45%;
            margin: 5px 0px !important;
          }
          .footer-column:nth-child(5) {
            width: 50%;
          }
          .footer-column:nth-child(6) {
            min-width: 161px;
          }
          .footer-column:nth-child(7) {
            order: 4;
            width: 48%;
            margin: 5px 0px !important;
            min-width: 169px;
          }
          .custom-gap {
            gap: 0px !important;
          }
          .social-icons {
            max-width: 12px;
            max-height: 12px;
          }
          .brand-descr-b {
            font-size: 10px;
          }
          .join-txt {
            max-width: 77px;
            white-space: wrap !important;
          }
        }

        @media (max-width: 768px) {
          .footer-container {
            background-image: none;
          }
          .social-icons {
            max-width: 12px;
            max-height: 12px;
          }
          .brand-descr-b {
            font-size: 10px;
          }
        }
        @media ((min-width: 990px) and (max-width: 1460px)) {
          .footer-column:nth-child(2) {
            order: 4;
          }
        }
        .custom-gap {
          gap: 22px;
        }
        .social-container {
          display: flex;
          justify-content: flex-end;
          align-items: center;
          flex-wrap: nowrap;
        }

        @media (max-width: 576px) {
          .social-container {
            /* justify-content: center; */
          }
          button.btn {
            padding: 5px 30px;
          }
          input.form-control {
            padding-left: 5px !important;
          }
          .badge-img {
            height: 67.2px;
            width: 69px;
            object-fit: contain;
          }
        }
        @media (max-width: 480px) {
          .comprehensive-txt {
            max-width: 187px;
          }
        }
        @media (max-width: 373px) {
          .footer-column:nth-child(6) {
            order: 4;
          }
          .footer-column:nth-child(7) {
            order: 3;
            min-width: 146px;
          }
        }
      `}</style>
    </footer>
  );
};

export default HomeFooter;
